import { Team, Tournament } from "@/types";
import { useMemo } from "react";
import { useCurrentTime } from "./useCurrentTime";

interface CooldownResult {
  isOnCooldown: boolean;
  remainingMs: number;
  totalCooldownMs: number;
  elapsedMs: number;
  progressPercent: number;
  cooldownEndDate: Date | null;
  cooldownStartDate: Date | null;
  formattedRemaining: string | null;
  formattedElapsed: string | null;
  formattedTotal: string | null;
}

/**
 * Calculates the remaining cooldown time for a team after a scheduled match.
 * Considers both challenge cooldown (for challenger/team1) and defend cooldown (for defender/team2).
 * Shows whichever cooldown ends later.
 *
 * @param team - The team to check cooldown for
 * @param tournament - Tournament configuration with challengeCooldownHours and defendCooldownHours
 * @returns Object with cooldown status and formatted remaining time
 */
export function useCooldownRemaining(
  team: Team | null,
  tournament: Tournament | null,
): CooldownResult {
  const nowMs = useCurrentTime();
  const now = useMemo(() => new Date(nowMs), [nowMs]);

  // Calculate cooldown start and end dates (picks whichever cooldown ends later)
  const { cooldownStartDate, cooldownEndDate, totalCooldownMs } =
    useMemo(() => {
      const noCooldown = {
        cooldownStartDate: null,
        cooldownEndDate: null,
        totalCooldownMs: 0,
      };

      if (!team || !tournament) return noCooldown;

      // Calculate challenge cooldown
      let challengeStart: Date | null = null;
      let challengeEnd: Date | null = null;
      let challengeTotalMs = 0;
      const challengeCooldownHours = tournament.challengeCooldownHours ?? 24;
      if (challengeCooldownHours > 0 && team.lastMatchScheduledAt) {
        const scheduledMatchTime =
          team.lastMatchScheduledAt instanceof Date
            ? team.lastMatchScheduledAt
            : new Date(team.lastMatchScheduledAt);
        challengeStart = new Date(scheduledMatchTime);
        challengeEnd = new Date(challengeStart);
        challengeEnd.setHours(challengeEnd.getHours() + challengeCooldownHours);
        challengeTotalMs = challengeCooldownHours * 60 * 60 * 1000;
      }

      // Calculate defend cooldown
      let defendStart: Date | null = null;
      let defendEnd: Date | null = null;
      let defendTotalMs = 0;
      const defendCooldownHours = tournament.defendCooldownHours ?? 0;
      if (defendCooldownHours > 0 && team.lastDefendMatchScheduledAt) {
        const scheduledMatchTime =
          team.lastDefendMatchScheduledAt instanceof Date
            ? team.lastDefendMatchScheduledAt
            : new Date(team.lastDefendMatchScheduledAt);
        defendStart = new Date(scheduledMatchTime);
        defendEnd = new Date(defendStart);
        defendEnd.setHours(defendEnd.getHours() + defendCooldownHours);
        defendTotalMs = defendCooldownHours * 60 * 60 * 1000;
      }

      // Pick whichever cooldown ends later
      if (!challengeEnd && !defendEnd) return noCooldown;

      if (!challengeEnd) {
        return {
          cooldownStartDate: defendStart,
          cooldownEndDate: defendEnd,
          totalCooldownMs: defendTotalMs,
        };
      }

      if (!defendEnd) {
        return {
          cooldownStartDate: challengeStart,
          cooldownEndDate: challengeEnd,
          totalCooldownMs: challengeTotalMs,
        };
      }

      // Both exist, pick the one that ends later
      if (challengeEnd > defendEnd) {
        return {
          cooldownStartDate: challengeStart,
          cooldownEndDate: challengeEnd,
          totalCooldownMs: challengeTotalMs,
        };
      }

      return {
        cooldownStartDate: defendStart,
        cooldownEndDate: defendEnd,
        totalCooldownMs: defendTotalMs,
      };
    }, [team, tournament]);

  // Calculate if currently on cooldown
  const isOnCooldown = useMemo(() => {
    if (!cooldownEndDate) return false;
    // Show cooldown if the end date hasn't passed yet
    // This includes both pre-match waiting and post-match cooldown
    return now < cooldownEndDate;
  }, [cooldownEndDate, now]);

  // Calculate remaining and elapsed milliseconds
  const { remainingMs, elapsedMs } = useMemo(() => {
    if (!cooldownEndDate || !cooldownStartDate || !isOnCooldown) {
      return { remainingMs: 0, elapsedMs: 0 };
    }

    // If cooldown hasn't started yet (still waiting for match to finish),
    // elapsed is 0 and remaining is the full cooldown duration
    if (now < cooldownStartDate) {
      return {
        remainingMs: totalCooldownMs,
        elapsedMs: 0,
      };
    }

    // Cooldown has started, calculate elapsed and remaining within the cooldown period
    const elapsed = now.getTime() - cooldownStartDate.getTime();
    const remaining = cooldownEndDate.getTime() - now.getTime();

    return {
      remainingMs: Math.max(0, remaining),
      elapsedMs: Math.max(0, elapsed),
    };
  }, [cooldownEndDate, cooldownStartDate, isOnCooldown, now, totalCooldownMs]);

  // Calculate progress percentage (0-100)
  const progressPercent = useMemo(() => {
    if (!isOnCooldown || totalCooldownMs <= 0) return 0;
    return Math.min(100, Math.max(0, (elapsedMs / totalCooldownMs) * 100));
  }, [isOnCooldown, elapsedMs, totalCooldownMs]);

  // Format remaining time as hh:mm:ss
  const formattedRemaining = useMemo(() => {
    if (!isOnCooldown || remainingMs <= 0) return null;

    const hours = Math.floor(remainingMs / (1000 * 60 * 60));
    const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remainingMs % (1000 * 60)) / 1000);

    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }, [isOnCooldown, remainingMs]);

  // Format elapsed time
  const formattedElapsed = useMemo(() => {
    if (!isOnCooldown) return null;

    // Show "0h" when cooldown hasn't started yet or elapsed is 0
    if (elapsedMs <= 0) return "0h";

    const hours = Math.floor(elapsedMs / (1000 * 60 * 60));
    const minutes = Math.floor((elapsedMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours >= 1) {
      return `${hours}h`;
    }
    return `${minutes}m`;
  }, [isOnCooldown, elapsedMs]);

  // Format total cooldown time
  const formattedTotal = useMemo(() => {
    if (totalCooldownMs <= 0) return null;

    const hours = Math.floor(totalCooldownMs / (1000 * 60 * 60));
    return `${hours}h`;
  }, [totalCooldownMs]);

  return {
    isOnCooldown,
    remainingMs,
    totalCooldownMs,
    elapsedMs,
    progressPercent,
    cooldownEndDate,
    cooldownStartDate,
    formattedRemaining,
    formattedElapsed,
    formattedTotal,
  };
}
